import { useState } from "react";
import { Option } from "@pages/Assistant/components/AssistantForm";
import { AssistantSelectedFiles } from "@services/ProjectChatContext";
import { renderMessagesForDisplay } from "./renderMessages";

export interface ChatMessage {
  sender: string;
  message: string;
  citations: any[]; // legacy (may be empty on new msgs)
  message_parts?: Array<any>; // NEW: [{ type: "text"|"citation", content: ... }]
  report_id: string | null;
  report_name: string | null;
  project_id: string;
  project_name: string;
  conversation_id: string;
  session_id: string;
  generated_images_s3_keys: any[];
  selected_files: any[] | null;
  // optional new flags (passed through by BE)
  enable_web_search?: boolean | null;
  enable_external_apis?: string[] | null;
  intermediate_steps?: string | null;
}

export const useConversationFetcher = ({
  getConversationById,
  setChatLog,
  setChatHistory,
  setSelectedProject,
  setSelectedReport,
  setSelectedFiles,
  setSidebarDisabled,
  setLoading,
  setFetchedChatCount,
  setChatSessionId,
  setConversationIdRef,
  setDocxContent,
  setExcelData,
  setShowDocxModal,
  setShowExcelModal,
  setEnabledSources,
  showAndSetModalImage,
}: {
  getConversationById: (id: number) => Promise<any>;
  setChatLog: (val: any[]) => void;
  setChatHistory: (val: any[]) => void;
  setSelectedProject: React.Dispatch<React.SetStateAction<Option>>;
  setSelectedReport: React.Dispatch<React.SetStateAction<Option>>;
  setSelectedFiles: React.Dispatch<
    React.SetStateAction<AssistantSelectedFiles[]>
  >;
  setSidebarDisabled: (val: boolean) => void;
  setLoading: (val: boolean) => void;
  setFetchedChatCount: (val: number) => void;
  setChatSessionId: (val: string | null) => void;
  setConversationIdRef: (val: string | null) => void;
  setDocxContent: (val: string) => void;
  setExcelData: (val: any[][]) => void;
  setShowDocxModal: (val: boolean) => void;
  setShowExcelModal: (val: boolean) => void;
  setEnabledSources: React.Dispatch<React.SetStateAction<Set<string>>>;
  showAndSetModalImage: (src: string, alt: string) => void;
}) => {
  const [error, setError] = useState<Error | null>(null);

  const fetchConversation = async (conversationId: string) => {
    if (!conversationId) return;

    try {
      setLoading(true);
      setSidebarDisabled(true);

      const fetchedConversation = await getConversationById(
        parseInt(conversationId, 10)
      );
      const messages: ChatMessage[] = fetchedConversation.messages || [];

      setFetchedChatCount(messages.length);

      // Render-ready messages for the chat log (bot messages wrapped with pills)
      const processedMessages = renderMessagesForDisplay(messages, {
        setShowDocxModal,
        setDocxContent,
        setShowExcelModal,
        setExcelData,
        showAndSetModalImage,
      });

      // Render these
      setChatLog(processedMessages);

      // Keep the raw messages (as returned by BE) in history
      setChatHistory(messages);

      // Infer latest context from the last user message
      const lastUser = messages.filter((m) => m.sender === "user").at(-1);

      if (lastUser?.project_id && lastUser?.project_name) {
        setSelectedProject({
          id: lastUser.project_id,
          name: lastUser.project_name,
        });
      } else {
        setSelectedProject({} as Option);
      }

      if (lastUser?.report_id && lastUser?.report_name) {
        setSelectedReport({
          id: lastUser.report_id,
          name: lastUser.report_name,
        });
      } else {
        setSelectedReport({} as Option);
      }

      if (lastUser?.selected_files && lastUser.selected_files.length > 0) {
        setSelectedFiles(lastUser.selected_files as AssistantSelectedFiles[]);
      } else {
        setSelectedFiles([]);
      }

      const enabled = new Set<string>(
        fetchedConversation.enabled_external_apis || []
      );
      setEnabledSources(enabled);

      setChatSessionId((fetchedConversation as any).chat_session_id ?? null);
      setConversationIdRef(conversationId);

      setChatSessionId(fetchedConversation.chat_session_id || null);
      setConversationIdRef(conversationId);
    } catch (err: any) {
      console.error(err);
      setError(err);
    } finally {
      setLoading(false);
      setSidebarDisabled(false);
    }
  };

  return { fetchConversation, error };
};
